// Tag color resolution. Two-stage:
//   1. If the tag matches `prefix:value` (e.g. `status:approved`,
//      `area:env`), hash the prefix to one of the palette colors
//      so every tag in the same namespace renders the same hue.
//   2. Otherwise the tag falls back to the default neutral color.
// The 12-color palette is WCAG-AA-AA contrast against the dark
// Mantine background for the chip's text. No-prefix tags stay
// gray on purpose: visual separation between user-authored tags
// and the structured `prefix:value` ones the team adopts as
// taxonomy matures (per #2c roadmap → Linear-style label groups).
//
// Reserved system tags (approved/wip/archived/final) get their
// own dedicated colors — they're workflow-meaningful and the
// muscle-memory color match across the app saves a glance.

const RESERVED_COLORS: Record<string, string> = {
  approved: 'green',
  wip:      'yellow',
  archived: 'gray',
  final:    'blue',
};

// Mantine semantic color names. Picked for spread across the
// color wheel so adjacent prefixes don't collide on hue. Each
// renders as a `variant="light"` Pill, which gives ~5:1 contrast
// against the dark Mantine app background.
const PALETTE = [
  'blue',
  'cyan',
  'teal',
  'green',
  'lime',
  'yellow',
  'orange',
  'red',
  'pink',
  'grape',
  'violet',
  'indigo',
];

// FNV-1a hash. Stable across reloads (no Math.random) and gives
// us a uniform-ish distribution across the palette.
function hashFnv1a(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

export function colorForTag(tag: string): string {
  const lower = tag.toLowerCase();
  const reserved = RESERVED_COLORS[lower];
  if (reserved) return reserved;
  const colonIdx = lower.indexOf(':');
  if (colonIdx > 0) {
    const prefix = lower.slice(0, colonIdx);
    return PALETTE[hashFnv1a(prefix) % PALETTE.length]!;
  }
  return 'gray';
}
